import { Backend, Blend, DynamicMesh, FLOATS_PER_VERTEX, Frame, MAX_DRAWS, Mesh, PACKED_STRIDE, quadIndices } from "./backend";

// The main backend: WebGPU. One shader with the same modes as the WebGL one
// (the shading must match backend-gl.ts), five pipelines — two vertex layouts
// by opaque / see-through / additive — and every draw's uniforms packed into
// one buffer, written once a frame and picked out by dynamic offset.

const SHADER = /* wgsl */ `
struct Globals {
  viewProj: mat4x4<f32>,
  cam: vec4<f32>,
  fog: vec4<f32>,
  time: vec4<f32>, // time, look
  sun: vec4<f32>,
  sky: vec4<f32>,
  ground: vec4<f32>,
};
struct Obj {
  model: mat4x4<f32>,
  tint: vec4<f32>,
  body: vec4<f32>,
  accent: vec4<f32>,
  skin: vec4<f32>,
  hair: vec4<f32>,
  params: vec4<f32>, // mode, shine
};
@group(0) @binding(0) var<uniform> g: Globals;
@group(0) @binding(1) var samp: sampler;
@group(0) @binding(2) var tex: texture_2d<f32>;
@group(1) @binding(0) var<uniform> o: Obj;

struct VOut {
  @builtin(position) pos: vec4<f32>,
  @location(0) normal: vec3<f32>,
  @location(1) color: vec4<f32>,
  @location(2) uv: vec2<f32>,
  @location(3) world: vec3<f32>,
};

fn transform(p: vec3<f32>, n: vec3<f32>, c: vec4<f32>, uv: vec2<f32>) -> VOut {
  var out: VOut;
  let w = o.model * vec4<f32>(p, 1.0);
  out.world = w.xyz;
  out.normal = (o.model * vec4<f32>(n, 0.0)).xyz;
  out.color = c;
  out.uv = uv;
  out.pos = g.viewProj * w;
  return out;
}

// MeshBuilder's floats. Colour arrives as three components; the fourth
// reads as 1, "no paint slot".
@vertex fn vsFloat(@location(0) p: vec3<f32>, @location(1) n: vec3<f32>, @location(2) c: vec4<f32>,
                   @location(3) uv: vec2<f32>) -> VOut {
  return transform(p, n, c, uv);
}

// A Blender model's 20-byte records: no uv.
@vertex fn vsPacked(@location(0) p: vec3<f32>, @location(1) n: vec4<f32>, @location(2) c: vec4<f32>) -> VOut {
  return transform(p, n.xyz, c, vec2<f32>(0.0, 0.0));
}

fn mod2(a: f32) -> f32 { return a - 2.0 * floor(a / 2.0); }

// A hash without sin (cheaper, and steady at mediump): Dave Hoskins's hash12.
// An ordered 4×4 dither, 0…1, by pixel.
fn bayer2(a: vec2<f32>) -> f32 { let b = floor(a); return fract(b.x / 2.0 + b.y * b.y * 0.75); }
fn bayer4(a: vec2<f32>) -> f32 { return bayer2(0.5 * a) * 0.25 + bayer2(a); }

fn hash(p: vec2<f32>) -> f32 {
  var q = fract(vec3<f32>(p.x, p.y, p.x) * 0.1031);
  q = q + dot(q, q.yzx + 33.33);
  return fract((q.x + q.y) * q.z);
}
// Value noise, 0…1.
fn noise(p: vec2<f32>) -> f32 {
  let i = floor(p);
  let f = fract(p);
  let u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2<f32>(1.0, 0.0)), u.x), mix(hash(i + vec2<f32>(0.0, 1.0)), hash(i + vec2<f32>(1.0, 1.0)), u.x), u.y);
}

// Modes: 0 lit colour, 1 road (checker + start line), 2 boost pad, 3 sprite,
// 4 soft blob (shadow, smoke), 5 exhaust flame, 6 ember glow, 7 light beam,
// 8 unlit (the sky, the hills round it), 9 lit with a grain (the ground, the road),
// 10 lit, faded by dithering to the tint's alpha (a kart in front of the camera).
@fragment fn fs(i: VOut) -> @location(0) vec4<f32> {
  let mode = o.params.x;
  // Sampled up front, where control flow is still uniform, as WGSL requires.
  let t = textureSample(tex, samp, i.uv);
  if (mode > 2.5 && mode < 3.5) {
    if (t.a < 0.45) { discard; }
    // Over its alpha: a mipmap's edge texels average in the transparent black round a sprite.
    return vec4<f32>(t.rgb / t.a * o.tint.rgb, 1.0);
  }
  if (mode > 3.5 && mode < 4.5) {
    let d = length(i.uv - vec2<f32>(0.5, 0.5)) * 2.0;
    return vec4<f32>(i.color.rgb, (1.0 - smoothstep(0.4, 1.0, d)) * o.tint.a);
  }
  if (mode > 4.5 && mode < 5.5) {
    // Along the flame (v: 0 at the pipe, 1 at the tip): white-hot, then
    // orange, then a red that fades out — licking, and softest at its edges.
    let v = i.uv.y;
    let lick = 0.75 + 0.25 * sin(g.time.x * 55.0 + i.uv.x * 25.0 - v * 14.0);
    var col = mix(vec3<f32>(1.0, 0.96, 0.78), vec3<f32>(1.0, 0.5, 0.08), smoothstep(0.05, 0.5, v));
    col = mix(col, vec3<f32>(0.85, 0.1, 0.02), smoothstep(0.5, 1.0, v));
    let edge = pow(abs(dot(normalize(i.normal), normalize(g.cam.xyz - i.world))), 0.7);
    let a = (1.0 - smoothstep(0.35, 1.0, v)) * lick * edge * o.tint.a;
    return vec4<f32>(col * o.tint.rgb * a * 1.2, 1.0);
  }
  if (mode > 7.5 && mode < 8.5) {
    return vec4<f32>(i.color.rgb, 1.0);
  }
  if (mode > 6.5 && mode < 7.5) {
    // A laser or a light's beam (u across it, v along it): brightest down
    // the middle, fading towards the far end.
    let across = 1.0 - abs(i.uv.x - 0.5) * 2.0;
    return vec4<f32>(o.tint.rgb * across * across * (1.0 - 0.75 * i.uv.y) * o.tint.a, 1.0);
  }
  if (mode > 5.5 && mode < 6.5) {
    let d = length(i.uv - vec2<f32>(0.5, 0.5)) * 2.0;
    return vec4<f32>(i.color.rgb * (1.0 - smoothstep(0.0, 1.0, d)), 1.0);
  }
  // Paint slots: a model's alpha says which colour goes there, over the
  // shading baked into its grey — 0 body, 64 skin, 128 accent, 192 hair,
  // 255 none.
  var base = i.color.rgb;
  if (i.color.a < 0.125) { base = base * o.body.rgb; }
  else if (i.color.a < 0.375) { base = base * o.skin.rgb; }
  else if (i.color.a < 0.625) { base = base * o.accent.rgb; }
  else if (i.color.a < 0.875) { base = base * o.hair.rgb; }
  // Faded by leaving out pixels, not by blending: what's behind the face — and
  // the face behind the head — stays hidden, as it does with the depth test.
  if (mode > 9.5 && o.tint.a <= bayer4(i.pos.xy)) { discard; }
  var lit = 1.0;
  if (mode > 0.5 && mode < 1.5) {
    let c = mod2(floor(i.uv.x * 3.0) + floor(i.uv.y / 4.67));
    base = i.color.rgb * (1.0 + 0.2 * c);
    if (i.uv.y < 2.4) {
      base = vec3<f32>(0.06 + 0.9 * mod2(floor(i.uv.x * 14.0) + floor(i.uv.y * 2.5)));
    }
  } else if (mode > 8.5 && mode < 9.5) {
    // Grain in world space, at two scales so neither shows a repeat.
    base = base * (0.9 + 0.13 * noise(i.world.xz * 0.35) + 0.08 * noise(i.world.xz * 2.7));
  } else if (mode > 1.5 && mode < 2.5) {
    let ch = fract(i.uv.y * 2.5 + abs(i.uv.x - 0.5) * 1.4 - g.time.x * 2.2);
    base = mix(vec3<f32>(1.0, 0.5, 0.05), vec3<f32>(1.0, 0.88, 0.25), step(0.5, ch));
    lit = 0.0;
  }
  let n = normalize(i.normal);
  let view = normalize(g.cam.xyz - i.world);
  if (g.time.y > 0.5) {
    // The revamp's light: a hemisphere of blue sky above and warm ground
    // below, a sun from over the camera's shoulder, a glossy highlight and a
    // fresnel of sky on the models; a touch more saturated, fogged into the horizon.
    let sun = g.sun.xyz;
    let hemi = mix(g.ground.rgb, g.sky.rgb, 0.5 + 0.5 * n.y);
    let light = mix(vec3<f32>(1.0), hemi + vec3<f32>(1.0, 0.93, 0.8) * max(dot(n, sun), 0.0) * 0.62, lit);
    let spec = pow(max(dot(n, normalize(sun + view)), 0.0), 60.0) * 0.55;
    let fres = pow(1.0 - max(dot(n, view), 0.0), 3.0) * 0.8;
    var col = (base * light + (vec3<f32>(spec) + g.sky.rgb * fres) * o.params.y) * o.tint.rgb;
    // Graded: 15 % more saturated, highlights a touch warmer and shadows cooler.
    let luma = dot(col, vec3<f32>(0.299, 0.587, 0.114));
    col = mix(vec3<f32>(luma), col, 1.15) + (luma - 0.5) * vec3<f32>(0.06, 0.0, -0.06);
    let fog = smoothstep(110.0, 300.0, distance(i.world, g.cam.xyz));
    return vec4<f32>(mix(col, g.fog.rgb, fog), o.tint.a);
  }
  let l = normalize(vec3<f32>(0.35, 1.0, 0.25));
  let diffuse = max(dot(n, l), 0.0);
  let light = mix(1.0, 0.45 + 0.2 * (0.5 + 0.5 * n.y) + 0.45 * diffuse, lit);
  // Toy plastic: a tight highlight and a soft rim, on the models only.
  let spec = pow(max(dot(n, normalize(l + view)), 0.0), 40.0) * 0.45;
  let rim = pow(1.0 - max(dot(n, view), 0.0), 3.0) * 0.22;
  let col = (base * light + vec3<f32>(spec + rim) * o.params.y) * o.tint.rgb;
  let fog = smoothstep(70.0, 170.0, distance(i.world, g.cam.xyz));
  return vec4<f32>(mix(col, g.fog.rgb, fog), o.tint.a);
}
`;

/** Each draw's uniforms: 160 bytes, padded out to the 256 a dynamic offset steps in. */
const OBJ_STRIDE = 256;
const OBJ_FLOATS = OBJ_STRIDE / 4;
const GLOBALS_SIZE = 160;
const SAMPLES = 4;
const PASSES: Blend[] = ["opaque", "alpha", "add"];

interface GpuMesh extends Mesh {
  vertices: GPUBuffer;
  indices: GPUBuffer;
}

const align4 = (n: number) => (n + 3) & ~3;

/**
 * Resolves to null — rather than throwing — on anything that can't or won't
 * give us a WebGPU device, and only then touches the canvas: once a canvas
 * has handed out a "webgpu" context it will never give a "webgl" one, so
 * asking first would leave the fallback nothing to draw on.
 */
export async function createGpuBackend(canvas: HTMLCanvasElement): Promise<Backend | null> {
  if (typeof navigator === "undefined" || !("gpu" in navigator)) return null;
  let device: GPUDevice;
  try {
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) return null;
    device = await adapter.requestDevice();
  } catch {
    return null;
  }
  const context = canvas.getContext("webgpu");
  if (!context) {
    device.destroy();
    return null;
  }
  const format = navigator.gpu.getPreferredCanvasFormat();
  context.configure({ device, format, alphaMode: "opaque" });

  const module = device.createShaderModule({ code: SHADER });
  const globalsEntries: GPUBindGroupLayoutEntry[] = [
    { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
    { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
    { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: {} },
  ];
  const objEntries: GPUBindGroupLayoutEntry[] = [
    {
      binding: 0,
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: { type: "uniform", hasDynamicOffset: true, minBindingSize: 160 },
    },
  ];
  const globalsLayout = device.createBindGroupLayout({ entries: globalsEntries });
  const objLayout = device.createBindGroupLayout({ entries: objEntries });
  const layout = device.createPipelineLayout({ bindGroupLayouts: [globalsLayout, objLayout] });

  const floatAttributes: GPUVertexAttribute[] = [
    { shaderLocation: 0, offset: 0, format: "float32x3" },
    { shaderLocation: 1, offset: 12, format: "float32x3" },
    { shaderLocation: 2, offset: 24, format: "float32x3" },
    { shaderLocation: 3, offset: 36, format: "float32x2" },
  ];
  const packedAttributes: GPUVertexAttribute[] = [
    { shaderLocation: 0, offset: 0, format: "float32x3" },
    { shaderLocation: 1, offset: 12, format: "snorm8x4" },
    { shaderLocation: 2, offset: 16, format: "unorm8x4" },
  ];
  const floatLayout: GPUVertexBufferLayout = { arrayStride: FLOATS_PER_VERTEX * 4, attributes: floatAttributes };
  const packedLayout: GPUVertexBufferLayout = { arrayStride: PACKED_STRIDE, attributes: packedAttributes };
  const blends: Record<Blend, GPUBlendState | undefined> = {
    opaque: undefined,
    alpha: {
      color: { srcFactor: "src-alpha", dstFactor: "one-minus-src-alpha" },
      alpha: { srcFactor: "one", dstFactor: "one-minus-src-alpha" },
    },
    add: { color: { srcFactor: "one", dstFactor: "one" }, alpha: { srcFactor: "one", dstFactor: "one" } },
  };
  const pipelines = new Map<string, GPURenderPipeline>();
  const pipeline = (packed: boolean, blend: Blend) => {
    const key = `${packed}-${blend}`;
    let p = pipelines.get(key);
    if (!p) {
      p = device.createRenderPipeline({
        layout,
        vertex: { module, entryPoint: packed ? "vsPacked" : "vsFloat", buffers: [packed ? packedLayout : floatLayout] },
        fragment: { module, entryPoint: "fs", targets: [{ format, blend: blends[blend] }] },
        primitive: { topology: "triangle-list", cullMode: "none" },
        // See-through and fire are tested against depth but don't write it.
        depthStencil: { format: "depth24plus", depthWriteEnabled: blend === "opaque", depthCompare: "less" },
        multisample: { count: SAMPLES },
      });
      pipelines.set(key, p);
    }
    return p;
  };

  const globalsBuffer = device.createBuffer({ size: GLOBALS_SIZE, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
  const globals = new Float32Array(GLOBALS_SIZE / 4);
  const objBuffer = device.createBuffer({ size: OBJ_STRIDE * MAX_DRAWS, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
  const objs = new Float32Array(OBJ_FLOATS * MAX_DRAWS);
  const objGroup = device.createBindGroup({ layout: objLayout, entries: [{ binding: 0, resource: { buffer: objBuffer, size: 160 } }] });
  const sampler = device.createSampler({ magFilter: "linear", minFilter: "linear" });

  let texture = device.createTexture({
    size: [1, 1],
    format: "rgba8unorm",
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
  });
  const makeGlobalsGroup = () =>
    device.createBindGroup({
      layout: globalsLayout,
      entries: [
        { binding: 0, resource: { buffer: globalsBuffer } },
        { binding: 1, resource: sampler },
        { binding: 2, resource: texture.createView() },
      ],
    });
  let globalsGroup = makeGlobalsGroup();

  let msaa: GPUTexture | null = null;
  let depth: GPUTexture | null = null;
  // Reused every frame, so drawing allocates as little as the API allows.
  const offsets = Uint32Array.from({ length: MAX_DRAWS }, (_, i) => i * OBJ_STRIDE);
  const colorAttachment: GPURenderPassColorAttachment = {
    view: undefined as unknown as GPUTextureView,
    clearValue: { r: 0, g: 0, b: 0, a: 1 },
    loadOp: "clear",
    storeOp: "discard",
  };
  const passDescriptor: GPURenderPassDescriptor = {
    colorAttachments: [colorAttachment],
    depthStencilAttachment: {
      view: undefined as unknown as GPUTextureView,
      depthClearValue: 1,
      depthLoadOp: "clear",
      depthStoreOp: "discard",
    },
  };

  const buffer = (data: BufferSource & { byteLength: number }, usage: number) => {
    const b = device.createBuffer({ size: align4(data.byteLength), usage: usage | GPUBufferUsage.COPY_DST, mappedAtCreation: true });
    const view = ArrayBuffer.isView(data) ? data : new Uint8Array(data as ArrayBuffer);
    new Uint8Array(b.getMappedRange()).set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));
    b.unmap();
    return b;
  };

  const backend: Backend = {
    kind: "webgpu",
    depthZeroToOne: true,
    lost: false,
    mesh(vertices, indices, packed): GpuMesh {
      return {
        vertices: buffer(vertices, GPUBufferUsage.VERTEX),
        indices: buffer(indices, GPUBufferUsage.INDEX),
        count: indices.length,
        packed,
      };
    },
    dynamicMesh(maxQuads): DynamicMesh & GpuMesh {
      const data = new Float32Array(maxQuads * 4 * FLOATS_PER_VERTEX);
      return {
        vertices: device.createBuffer({ size: data.byteLength, usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST }),
        indices: buffer(quadIndices(maxQuads), GPUBufferUsage.INDEX),
        data,
        quads: 0,
        maxQuads,
        count: 0,
        packed: false,
      };
    },
    updateDynamic(mesh) {
      const m = mesh as DynamicMesh & GpuMesh;
      m.count = m.quads * 6;
      if (m.quads) device.queue.writeBuffer(m.vertices, 0, m.data.buffer, m.data.byteOffset, m.quads * 4 * FLOATS_PER_VERTEX * 4);
    },
    free(mesh) {
      const m = mesh as GpuMesh;
      m.vertices.destroy();
      m.indices.destroy();
    },
    texture(source) {
      texture.destroy();
      texture = device.createTexture({
        size: [source.width, source.height],
        format: "rgba8unorm",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT,
      });
      device.queue.copyExternalImageToTexture({ source }, { texture }, [source.width, source.height]);
      globalsGroup = makeGlobalsGroup();
    },
    resize(width, height) {
      // Claim the canvas again: in development a renderer mounted and thrown
      // away a moment ago may have configured it for its own, now dead, device.
      context.configure({ device, format, alphaMode: "opaque" });
      canvas.width = Math.max(1, width);
      canvas.height = Math.max(1, height);
      msaa?.destroy();
      depth?.destroy();
      const size = [canvas.width, canvas.height];
      msaa = device.createTexture({ size, format, sampleCount: SAMPLES, usage: GPUTextureUsage.RENDER_ATTACHMENT });
      depth = device.createTexture({ size, format: "depth24plus", sampleCount: SAMPLES, usage: GPUTextureUsage.RENDER_ATTACHMENT });
      colorAttachment.view = msaa.createView();
      passDescriptor.depthStencilAttachment!.view = depth.createView();
    },
    render(frame: Frame) {
      if (backend.lost || !msaa || !depth) return;
      globals.set(frame.viewProj, 0);
      globals.set(frame.eye, 16);
      globals.set(frame.fog, 20);
      globals[24] = frame.time;
      globals[25] = frame.look;
      globals.set(frame.light, 28);
      device.queue.writeBuffer(globalsBuffer, 0, globals);

      for (let i = 0; i < frame.count; i++) {
        const d = frame.draws[i];
        const o = i * OBJ_FLOATS;
        objs.set(d.model, o);
        objs.set(d.tint, o + 16);
        const paint = d.paint;
        if (paint) {
          objs.set(paint.body, o + 20);
          objs.set(paint.accent, o + 24);
          objs.set(paint.skin, o + 28);
          objs.set(paint.hair, o + 32);
        } else objs.fill(1, o + 20, o + 36);
        objs[o + 36] = d.mode;
        objs[o + 37] = paint ? 1 : 0;
      }
      if (frame.count) device.queue.writeBuffer(objBuffer, 0, objs.buffer, 0, frame.count * OBJ_STRIDE);

      const clear = colorAttachment.clearValue as GPUColorDict;
      [clear.r, clear.g, clear.b] = frame.fog;
      colorAttachment.resolveTarget = context.getCurrentTexture().createView();
      const encoder = device.createCommandEncoder();
      const pass = encoder.beginRenderPass(passDescriptor);
      pass.setBindGroup(0, globalsGroup);
      let bound: GPURenderPipeline | null = null;
      for (const blend of PASSES) {
        for (let i = 0; i < frame.count; i++) {
          const d = frame.draws[i];
          if (d.blend !== blend) continue;
          const m = d.mesh as GpuMesh;
          const p = pipeline(m.packed, blend);
          if (p !== bound) {
            pass.setPipeline(p);
            bound = p;
          }
          pass.setBindGroup(1, objGroup, offsets, i, 1);
          pass.setVertexBuffer(0, m.vertices);
          pass.setIndexBuffer(m.indices, "uint16");
          pass.drawIndexed(m.count, 1, m.first ?? 0);
        }
      }
      pass.end();
      device.queue.submit([encoder.finish()]);
    },
    destroy() {
      msaa?.destroy();
      depth?.destroy();
      texture.destroy();
      globalsBuffer.destroy();
      objBuffer.destroy();
      // Deliberately not context.unconfigure(): in development React mounts
      // everything twice, so a second renderer may already have this canvas;
      // configuring again replaces the old configuration on its own.
      device.destroy();
    },
  };
  device.lost.then(() => {
    backend.lost = true;
  });
  return backend;
}
